import { useQueryWithErrorHandler } from './useQueryWithErrorHandler';
import { useMemo } from 'react';
import { IResponseData, IError, IApiParams } from 'types';

import { useAuthClient } from './useAuthClient';
import { useAuth, useError } from '../providers';
import { firebaseAnalytics } from '../analytics';
import { errors } from '../config';

const removeUserQueryKey = 'projectMembers';

export function useRemoveUserApi({
  queryParameters,
  organizationId,
}: IApiParams) {
  const authClient = useAuthClient();
  const { openModal } = useError();
  const { supplierId } = useAuth();

  const currentSupplierId = useMemo(() => {
    return organizationId || supplierId;
  }, [organizationId, supplierId]);

  const removeUserUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/users/delete-user`;
  }, [currentSupplierId]);

  const {
    data: removeUserResponse,
    refetch: removeUser,
    isLoading: isRemovingUser,
    error: removeUserError,
  } = useQueryWithErrorHandler<IResponseData<boolean>, IError>({
    queryKey: [removeUserQueryKey, { queryParameters }],

    queryFn: async () => {
      return await authClient(`${removeUserUrl}`, {
        queryParameters,
        method: 'DELETE',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,
    retry: false,

    onError: (error: IError) => {
      firebaseAnalytics.logErrorEvent(errors.removeUser, error, {
        removeUserUrl,
        ...queryParameters,
      });
      openModal(error);
    },
  });

  return {
    removeUserResponse,
    removeUser,
    isRemovingUser,
    removeUserError,
  };
}
